import { useSelect } from '@wordpress/data';
import { SelectControl, Spinner, ToggleControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useState, useEffect, useRef } from '@wordpress/element';
/**
 * Internal dependencies
 */
import { ControlPanelControl } from '@vayu-blocks';

const ContentSelect = ({ label, postType, selectedPost, onChange }) => {
    // Fetch the posts, pages, or products dynamically based on postType
    const { content, isLoading } = useSelect(
        (select) => {
            const { getEntityRecords } = select('core');
            if (postType && postType !== 'none') {
                const posts = getEntityRecords('postType', postType, { per_page: -1 });
                return {
                    content: posts,
                    isLoading: posts === null, // Corrected to check for null (loading state)
                };
            }
            return { content: [], isLoading: false };
        },
        [postType]
    );

    // Set default selectedPost to the first item if none is selected
    useEffect(() => {
        if (!selectedPost && content && content.length > 0) {
            const firstPostId = content[0].id;
           
            onChange(firstPostId);
        }
    }, [content, selectedPost, onChange]);

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <>
            {content && content.length > 0 ? (
                <SelectControl
                    label={label}
                    value={selectedPost || ''} // Ensure controlled component
                    options={[
                        { label: __('Select an item', 'vayu-blocks'), value: '' }, // Placeholder option
                        ...content.map((item) => ({
                            label: item.title.rendered,
                            value: item.id,
                        })),
                    ]}
                    onChange={(newValue) => onChange(parseInt(newValue) || null)} // Ensure integer or null
                />
            ) : (
                <p>{__('No items found for this post type.', 'vayu-blocks')}</p>
            )}
        </>
    );
};

const PostTypeSelect = ({ attributes, setAttributes, context }) => {
    const { parentBlockName } = useSelect((select) => {
        const block = select('core/block-editor').getSelectedBlock();
        const parentBlock = block ? select('core/block-editor').getBlockParents(block.clientId) : [];
        const parentBlockName = parentBlock.length > 0 ? select('core/block-editor').getBlock(parentBlock[0])?.name : null;

        return {
            parentBlockName,
        };
    }, []);

    // Check if the parent block is `advance-query-loop`
    const isParentAdvanceQueryLoop = parentBlockName === 'vayu-blocks/advance-query-loop';

    const { dynamicPostType, selectedPost, selectedSourceField, contentLinkEnable, contentLinkUrl } = attributes;

    // Use ref to track initial render
    const isInitialRender = useRef(true);

    // Handler for post type change
    const onPostTypeChange = (newPostType) => {
       
        setAttributes({
            dynamicPostType: newPostType,
            selectedPost: null, // Reset to allow ContentSelect to set default
            selectedSourceField: 'none',
        });
    };

    // Handler for content selection
    const onContentChange = (selectedId) => {
        const parsedId = parseInt(selectedId, 10) || null;
        
        setAttributes({ selectedPost: parsedId });
    };

    // Handler for source field selection
    const onSourceFieldChange = (newSourceField) => {
        
        setAttributes({ selectedSourceField: newSourceField });
    };

    // Fetch posts and loading state
    const { isLoading } = useSelect(
        (select) => {
            const { getEntityRecords } = select('core');
            const posts = dynamicPostType && dynamicPostType !== 'none'
                ? getEntityRecords('postType', dynamicPostType, { per_page: -1 })
                : [];
            return { isLoading: posts === null };
        },
        [dynamicPostType]
    );

    // Trigger handlers on mount for pre-selected values
    useEffect(() => {
       

        if (isInitialRender.current) {
            if (selectedPost !== null && selectedPost !== undefined) {
                onContentChange(selectedPost);
            }
            if (selectedSourceField && selectedSourceField !== 'none') {
                onSourceFieldChange(selectedSourceField);
            }
            isInitialRender.current = false;
        }
    }, [selectedPost, selectedSourceField]);

    return (
        <div>
            {!isParentAdvanceQueryLoop && (
                <>
                    <ControlPanelControl
                        label={__('Dynamic Content', 'vayu-blocks')}
                        attributes={attributes}
                        setAttributes={setAttributes}
                        resetValues={{
                            dynamiControl: false,
                            dynamicPostType: 'none',
                            contentLinkEnable: false,
                            contentLinkUrl: 'none',
                        }}
                        onClick={() => setAttributes({ dynamiControl: true })}
                    >
                        <SelectControl
                            label={__('Content Type', 'vayu-blocks')}
                            value={dynamicPostType || 'none'}
                            options={[
                                { label: __('None', 'vayu-blocks'), value: 'none' },
                                { label: __('Post', 'vayu-blocks'), value: 'post' },
                                { label: __('Page', 'vayu-blocks'), value: 'page' },
                                { label: __('Product', 'vayu-blocks'), value: 'product' },
                            ]}
                            onChange={onPostTypeChange}
                        />

                        {(dynamicPostType === 'post' || dynamicPostType === 'page' || dynamicPostType === 'product') && (
                            <>
                                <ContentSelect
                                    label={__('Select ' + dynamicPostType.charAt(0).toUpperCase() + dynamicPostType.slice(1), 'vayu-blocks')}
                                    postType={dynamicPostType}
                                    selectedPost={selectedPost}
                                    onChange={onContentChange}
                                />

                                {!isLoading && (
                                    <>
                                        <SelectControl
                                            label={__('Select Source Field', 'vayu-blocks')}
                                            value={selectedSourceField || 'none'}
                                            onChange={onSourceFieldChange}
                                        >
                                            <optgroup label={__('None', 'vayu-blocks')}>
                                                <option value="none">{__('none', 'vayu-blocks')}</option>
                                            </optgroup>
                                            <optgroup label={__('Post', 'vayu-blocks')}>
                                                <option value="title">{__('Post Title', 'vayu-blocks')}</option>
                                                <option value="slug">{__('Post Slug', 'vayu-blocks')}</option>
                                                <option value="content">{__('content', 'vayu-blocks')}</option>
                                                <option value="excerpt">{__('Post Excerpt', 'vayu-blocks')}</option>
                                                <option value="post_date">{__('Post Date', 'vayu-blocks')}</option>
                                                <option value="post_time">{__('Post Time', 'vayu-blocks')}</option>
                                                <option value="post_id">{__('Post Id', 'vayu-blocks')}</option>
                                                <option value="post_image">{__('Post Featured Image', 'vayu-blocks')}</option>
                                            </optgroup>
                                            <optgroup label={__('Author', 'vayu-blocks')}>
                                                <option value="author_name">{__('Name', 'vayu-blocks')}</option>
                                                <option value="author_nic_name">{__('Nic Name', 'vayu-blocks')}</option>
                                                <option value="author_first_name">{__('First Name', 'vayu-blocks')}</option>
                                                <option value="author_last_name">{__('Last Name', 'vayu-blocks')}</option>
                                                <option value="author_bio">{__('Bio', 'vayu-blocks')}</option>
                                                <option value="author_email">{__('Email', 'vayu-blocks')}</option>
                                                <option value="author_image">{__('Image', 'vayu-blocks')}</option>
                                            </optgroup>
                                            <optgroup label={__('Comment', 'vayu-blocks')}>
                                                <option value="comment_count">{__('Comment Count', 'vayu-blocks')}</option>
                                            </optgroup>
                                        </SelectControl>

                                        <ToggleControl
                                            label={__('Enable Link', 'vayu-blocks')}
                                            checked={contentLinkEnable}
                                            onChange={(contentLinkEnable) => setAttributes({ contentLinkEnable })}
                                        />
                                        {contentLinkEnable && (
                                            <SelectControl
                                                label={__('Select Source Url', 'vayu-blocks')}
                                                value={contentLinkUrl || 'none'}
                                                onChange={(contentLinkUrl) => setAttributes({ contentLinkUrl })}
                                            >
                                                <optgroup label={__('None', 'vayu-blocks')}>
                                                    <option value="none">{__('none', 'vayu-blocks')}</option>
                                                </optgroup>
                                                <optgroup label={__('Post', 'vayu-blocks')}>
                                                    <option value="post_url">{__('Post Url', 'vayu-blocks')}</option>
                                                </optgroup>
                                                <optgroup label={__('Archive', 'vayu-blocks')}>
                                                    <option value="archive_url">{__('Archive Url', 'vayu-blocks')}</option>
                                                </optgroup>
                                                <optgroup label={__('Author', 'vayu-blocks')}>
                                                    <option value="author_url">{__('Author Url', 'vayu-blocks')}</option>
                                                </optgroup>
                                                <optgroup label={__('Site', 'vayu-blocks')}>
                                                    <option value="site_url">{__('Site Url', 'vayu-blocks')}</option>
                                                </optgroup>
                                                <optgroup label={__('Comments', 'vayu-blocks')}>
                                                    <option value="comments_url">{__('Comments Url', 'vayu-blocks')}</option>
                                                </optgroup>
                                                <optgroup label={__('Media', 'vayu-blocks')}>
                                                    <option value="featured_img_url">{__('Featured Image Url', 'vayu-blocks')}</option>
                                                </optgroup>
                                            </SelectControl>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </ControlPanelControl>
                </>
            )}

            {isParentAdvanceQueryLoop && (
                <>
                    <ControlPanelControl
                        label={__('Dynamic Content', 'vayu-blocks')}
                        attributes={attributes}
                        setAttributes={setAttributes}
                        resetValues={{
                            dynamiControlloop: false,
                            contentLinkEnable: false,
                            contentLinkUrl: 'none',
                        }}
                        onClick={() => setAttributes({ dynamiControlloop: true })}
                    >
                        <SelectControl
                            label={__('Select Source Field', 'vayu-blocks')}
                            value={selectedSourceField || 'none'}
                            onChange={onSourceFieldChange}
                        >
                            <optgroup label={__('None', 'vayu-blocks')}>
                                <option value="none">{__('none', 'vayu-blocks')}</option>
                            </optgroup>
                            <optgroup label={__('Post', 'vayu-blocks')}>
                                <option value="title">{__('Post Title', 'vayu-blocks')}</option>
                                <option value="slug">{__('Post Slug', 'vayu-blocks')}</option>
                                <option value="excerpt">{__('Post Excerpt', 'vayu-blocks')}</option>
                                <option value="content">{__('Post Content', 'vayu-blocks')}</option>
                                <option value="post_date">{__('Post Date', 'vayu-blocks')}</option>
                                <option value="post_time">{__('Post Time', 'vayu-blocks')}</option>
                                <option value="post_id">{__('Post Id', 'vayu-blocks')}</option>
                                <option value="post_image">{__('Post Featured Image', 'vayu-blocks')}</option>
                            </optgroup>
                            <optgroup label={__('Author', 'vayu-blocks')}>
                                <option value="author_name">{__('Name', 'vayu-blocks')}</option>
                                <option value="author_nic_name">{__('Nic Name', 'vayu-blocks')}</option>
                                <option value="author_first_name">{__('First Name', 'vayu-blocks')}</option>
                                <option value="author_last_name">{__('Last Name', 'vayu-blocks')}</option>
                                <option value="author_bio">{__('Bio', 'vayu-blocks')}</option>
                                <option value="author_email">{__('Email', 'vayu-blocks')}</option>
                                <option value="author_image">{__('Image', 'vayu-blocks')}</option>
                            </optgroup>
                            <optgroup label={__('Comment', 'vayu-blocks')}>
                                <option value="comment_count">{__('Comment Count', 'vayu-blocks')}</option>
                            </optgroup>
                        </SelectControl>
                        <ToggleControl
                            label={__('Enable Link', 'vayu-blocks')}
                            checked={contentLinkEnable}
                            onChange={(contentLinkEnable) => setAttributes({ contentLinkEnable })}
                        />
                        {contentLinkEnable && (
                            <SelectControl
                                label={__('Select Source Url', 'vayu-blocks')}
                                value={contentLinkUrl || 'none'}
                                onChange={(contentLinkUrl) => setAttributes({ contentLinkUrl })}
                            >
                                <optgroup label={__('None', 'vayu-blocks')}>
                                    <option value="none">{__('none', 'vayu-blocks')}</option>
                                </optgroup>
                                <optgroup label={__('Post', 'vayu-blocks')}>
                                    <option value="post_url">{__('Post Url', 'vayu-blocks')}</option>
                                </optgroup>
                                <optgroup label={__('Archive', 'vayu-blocks')}>
                                    <option value="archive_url">{__('Archive Url', 'vayu-blocks')}</option>
                                </optgroup>
                                <optgroup label={__('Author', 'vayu-blocks')}>
                                    <option value="author_url">{__('Author Url', 'vayu-blocks')}</option>
                                </optgroup>
                                <optgroup label={__('Site', 'vayu-blocks')}>
                                    <option value="site_url">{__('Site Url', 'vayu-blocks')}</option>
                                </optgroup>
                                <optgroup label={__('Comments', 'vayu-blocks')}>
                                    <option value="comments_url">{__('Comments Url', 'vayu-blocks')}</option>
                                </optgroup>
                                <optgroup label={__('Media', 'vayu-blocks')}>
                                    <option value="featured_img_url">{__('Featured Image Url', 'vayu-blocks')}</option>
                                </optgroup>
                            </SelectControl>
                        )}
                    </ControlPanelControl>
                </>
            )}
        </div>
    );
};

export default PostTypeSelect;